import { TABLE_TOP_CORNERS } from "../../../v2/components/tableHeaderCorners.js";
// Pinned portfolio Overview for one week: a manually maintained Overall
// summary (portfolio-scoped, never stored on a project) plus an at-a-glance
// rollup of the FULL selected-week collection, independent of rail filters.
// No project media appears here.

import React, { useEffect, useRef, useState } from "react";
import { T } from "../../../app/tokens.js";
import { useEditablePanel } from "../../../hooks/useEditablePanel.js";
import { savePortfolioSummary } from "../../../data/projectWeeklyClient.js";
import { StatusPill } from "../../../v2/components/StatusPill.jsx";
import { OVERALL_STATUS_META, fmtTimestamp, milestoneText } from "./weeklyModel.js";
import { MilestoneSignal } from "../ProjectDisplaySignals.jsx";
import { AiStubNotice, aiBtn } from "./WeeklyProjectCard.jsx";
import { btn, btnPrimary, inputStyle, muted, withDisabled } from "./weeklyUi.js";

const label = { fontSize: T.fsXxs, fontWeight: T.fwSemibold, letterSpacing: ".05em", textTransform: "uppercase", color: T.mid };
const editBtn = { fontSize: T.fsXs, fontWeight: T.fwBold, color: T.blue, background: T.card, border: `1px solid ${T.blue}`, borderRadius: 8, padding: "4px 11px", cursor: "pointer" };

function OverallSummary({ data, weekDate, can, frozen, onMutate, guardRef }) {
  const has = Object.prototype.hasOwnProperty.call(data || {}, "portfolioSummary");
  const summary = data?.portfolioSummary || null;
  const panel = useEditablePanel(() => ({ narrative: summary?.narrative || "" }));
  const [stub, setStub] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const editing = can && panel.editing;
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);

  const dirtyRef = useRef(false);
  dirtyRef.current = editing;
  useEffect(() => {
    if (!guardRef) return undefined;
    guardRef.current = () => dirtyRef.current;
    return () => { guardRef.current = null; };
  }, [guardRef]);
  useEffect(() => {
    if (!editing) return undefined;
    const onBefore = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBefore);
    return () => window.removeEventListener("beforeunload", onBefore);
  }, [editing]);

  const open = (asStub) => { setError(null); setStub(asStub); panel.setEditing(true); };
  const cancel = () => { panel.reset(); setStub(false); setError(null); };
  const save = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await onMutate(() => savePortfolioSummary({ weekDate, narrative: panel.draft.narrative }));
      if (mounted.current) { panel.setEditing(false); setStub(false); }
    } catch (err) {
      if (mounted.current) setError(err?.message || "The summary could not be saved. Your draft is kept.");
    } finally { if (mounted.current) setBusy(false); }
  };
  const disabled = busy || !(panel.draft.narrative || "").trim();

  return (
    <section data-testid="portfolio-summary" className="wk-ecard">
      <header className="wk-ecard-hd">
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap", minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>Overall summary</h3>
          {summary && <span data-testid="portfolio-summary-provenance" style={{ fontSize: T.fsXs, color: T.mid, fontWeight: T.fwSemibold }}>{summary.authorName || "Unattributed"}{summary.updatedAt ? ` · ${fmtTimestamp(summary.updatedAt)}` : ""}</span>}
        </div>
        {can && !editing && (
           <span className="wk-actions" style={{ display: "inline-flex", gap: 6, flexWrap: "wrap" }}>
            <button type="button" data-testid="portfolio-ai-draft" onClick={() => open(true)} style={aiBtn}>✨ Draft overall summary</button>
            <button type="button" data-testid="portfolio-edit" onClick={() => open(false)} style={editBtn}>Edit</button>
          </span>
        )}
      </header>
      <div style={{ padding: "14px 17px" }}>
        {editing ? (
          <div style={{ background: T.blueSoft, border: `1px solid ${T.border}`, borderRadius: 11, padding: "12px 13px" }}>
            {stub && <AiStubNotice testId="portfolio-ai-stub" />}
            <textarea aria-label="Overall portfolio summary" data-testid="portfolio-summary-input" rows={6} disabled={busy} value={panel.draft.narrative} onChange={(e) => panel.setField("narrative", e.target.value)} style={inputStyle} />
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button type="button" data-testid="portfolio-summary-save" disabled={disabled} onClick={save} style={withDisabled(btnPrimary, disabled)}>{busy ? "Saving..." : "Save"}</button>
              <button type="button" data-testid="portfolio-summary-cancel" disabled={busy} onClick={cancel} style={btn}>Cancel</button>
            </div>
          </div>
        ) : summary?.narrative ? (
          <p data-testid="portfolio-summary-text" style={{ margin: 0, fontSize: T.fsBase, lineHeight: 1.55, color: T.dark, whiteSpace: "pre-wrap" }}>{summary.narrative}</p>
        ) : (
          <div data-testid="portfolio-summary-empty" style={{ color: T.light, fontStyle: "italic", fontSize: T.fsSm }}>
            {!has && frozen ? "The portfolio summary was not captured with this archive." : "Overall summary not yet posted for this week."}
          </div>
        )}
        {error && <div role="alert" data-testid="portfolio-summary-error" style={{ color: T.red, fontSize: T.fsXs, marginTop: 6 }}>{error}</div>}
      </div>
    </section>
  );
}

function Rollup({ projects, onOpenProject, busy }) {
  return (
    <section data-testid="portfolio-rollup" className="wk-ecard">
      <header className="wk-ecard-hd">
        <h3 style={{ margin: 0, fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark }}>By project · at a glance</h3>
        <span style={muted}>{projects.length} project{projects.length === 1 ? "" : "s"} this week</span>
      </header>
      {projects.length === 0 ? (
        <div style={{ padding: "14px 17px", color: T.light, fontStyle: "italic", fontSize: T.fsSm }}>No projects in this week's collection.</div>
      ) : (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {projects.map((p) => {
            const meta = OVERALL_STATUS_META[p.summary?.statusOverall] || OVERALL_STATUS_META[p.recordStatus] || null;
            const open = Array.isArray(p.decisions) ? p.decisions.filter((d) => !d.status || d.status === "open").length : null;
            const dates = !Object.hasOwn(p, "nextMilestone") ? "Key dates unavailable"
              : p.nextMilestone?.name ? `${milestoneText(p.nextMilestone)}${p.nextMilestone.dueDate ? "" : " · Date not recorded"}`
              : "None scheduled";
            return (
              <li key={p.id} style={{ borderBottom: `1px solid ${T.lightGray}` }}>
                <button type="button" data-testid={`portfolio-row-${p.id}`} disabled={busy} onClick={() => onOpenProject(p.id)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: "10px 17px", border: 0, background: "transparent", textAlign: "left", cursor: "pointer", color: T.dark, flexWrap: "wrap" }}>
                  <span style={{ flex: "1 1 180px", minWidth: 0 }}>
                    <span style={{ display: "block", fontSize: T.fsSm, fontWeight: T.fwBold }}>{p.name}</span>
                    <span style={{ display: "block", fontSize: T.fsXxs, color: T.mid }}>{p.phaseLabel || "Phase not set"} · Next: {dates} <MilestoneSignal milestone={p.nextMilestone} timeZone={p.projectTimeZone} /></span>
                  </span>
                  <span data-testid={`portfolio-decisions-${p.id}`} style={{ fontSize: T.fsXxs, fontWeight: T.fwBold, color: open ? T.blue : T.light, whiteSpace: "nowrap" }}>{open === null ? "Decisions unavailable" : `${open} open decision${open === 1 ? "" : "s"}`}</span>
                  {meta ? <StatusPill size="sm" value={meta.label} fg={meta.color} bg={meta.bg} testId={`portfolio-status-${p.id}`} /> : <StatusPill size="sm" value="Health not set" testId={`portfolio-status-${p.id}`} />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export function PortfolioWeeklyOverview({ legacy = false, data, projects, weekDate, writable, frozen, onMutate, onOpenProject, guardRef, busy }) {
  const can = Boolean(writable && data?.capabilities?.canPortfolioSummary);
  return (
    <article data-testid="weekly-portfolio-overview" aria-label="Portfolio overview">
      <div className="wk-dhead" style={{ paddingBottom: 14, ...TABLE_TOP_CORNERS }}>
        <h2 style={{ margin: 0, fontSize: T.fsXl || 22, fontWeight: T.fwBold, color: T.dark, lineHeight: 1.15 }}>Overview</h2>
        <div style={{ ...muted, marginTop: 6 }}>Whole portfolio for this week{!writable ? " · read-only" : ""}</div>
      </div>
      {legacy ? <div className="wk-body"><div data-testid="portfolio-unavailable" style={{ border: `1px dashed ${T.border}`, borderRadius: 14, padding: "28px 24px", background: T.card }}>
        <div style={{ fontSize: T.fsBase, fontWeight: T.fwBold, color: T.dark, marginBottom: 4 }}>Overview unavailable for this archive</div>
        <p style={{ margin: 0, ...muted, fontSize: T.fsSm, lineHeight: 1.5 }}>This week was frozen before Updates existed. No portfolio summary or project rollup was captured, and nothing is rebuilt from today's data.</p>
      </div></div> : <div className="wk-body">
        <OverallSummary key={weekDate} data={data} weekDate={weekDate} can={can} frozen={frozen} onMutate={onMutate} guardRef={guardRef} />
        <Rollup projects={projects} onOpenProject={onOpenProject} busy={busy} />
      </div>}
    </article>
  );
}
